import clsx from "clsx";
import moment from "moment/moment";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";

import actions from "@/actions";
import { FooterPager } from "@/components/common/FooterPager";
import { GridCell } from "@/components/list/Schedule/components/GridCell";
import { ScheduleHistoryDialog } from "@/components/list/Schedule/components/ScheduleHistoryDialog";
import { HISTORY_ALERT_COLOR, SCHEDULE_DATA_PER_PAGE } from "@/components/list/Schedule/constants";
import { useResultTextEditor } from "@/components/list/Schedule/hooks/useResultTextEditor";
import { ResultItem, ScheduleHistory, ScheduleResult } from "@/components/list/Schedule/types";
import { getConstructionId, omitString } from "@/lib/common";
import storageManager from "@/lib/storageManager";
import { RootState } from "@/reducers/types";

type Props = {
  data?: ScheduleResult;
  fetching: boolean;
  scheduleDate: Date;
  total: number;
  onPrevPage: () => void;
  onNextPage: () => void;
  onJumpPage: (page) => void;
};

export const ResultList: FC<Props> = ({ data, fetching, scheduleDate, total, onPrevPage, onNextPage, onJumpPage }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const constructionId = getConstructionId();
  const { currentPageOfResultList, isLoadingResults } = useSelector((state: RootState) => state.schedule);
  const [page, pageTotal] = useMemo(() => {
    return [total === 0 ? 0 : currentPageOfResultList, Math.ceil(total / SCHEDULE_DATA_PER_PAGE)];
  }, [total, currentPageOfResultList]);

  // 予定表項目情報
  const scheduleFields = useSelector((state: RootState) => state.schedule.fields);
  const [customLabelNumberOfWorkers, displayNumberOfWorkers] = useMemo(() => {
    const locale = storageManager.getLocale();
    const numberOfWorkersField = scheduleFields.find((v) => v.field_key === "field_t1");
    return [
      locale === "ja" ? numberOfWorkersField?.field_label_jp : numberOfWorkersField?.field_label_en,
      numberOfWorkersField?.active,
    ];
  }, [scheduleFields]);

  const headerDateLabel = useMemo(() => {
    return moment(scheduleDate).subtract(1, "day").format("MM/DD");
  }, [scheduleDate]);

  const isToday = useMemo(() => {
    const today = moment();
    return moment(scheduleDate).subtract(1, "day").isSame(today, "day");
  }, [scheduleDate]);

  const tableBodyRef = useRef<HTMLDivElement>();
  useEffect(() => {
    if (tableBodyRef.current?.scrollTop) {
      tableBodyRef.current.scrollTop = 0;
    }
  }, [currentPageOfResultList]);

  const handleClickItemName = (item: ResultItem) => {
    const query = new URLSearchParams();
    query.set("category_id", item.category_id.toString());
    query.set("primary_charge_id", item.primary_charge_id.toString());
    query.set("item_id", item.item_id.toString());
    window.open(`/#/${constructionId}/matrix?${query.toString()}`, "_blank", "noreferrer");
  };

  const textEditor = useResultTextEditor();

  const [tooltipText, setTooltipText] = useState(null);
  const setTooltip = (text) => {
    setTooltipText(text);
    ReactTooltip.rebuild();
  };

  const [historyData, setHistoryData] = useState<{ title: string; target: string; list: ScheduleHistory[] }>(null);
  const handleClickHistory = (item: ResultItem, targetColumn: string, targetName: string) => {
    dispatch(
      actions.schedule.getResultHistories(
        { result_id: item.result_task_id, target_column: targetColumn },
        (list: ScheduleHistory[]) => {
          setHistoryData({
            title: item.item_name,
            target: targetName,
            list,
          });
        }
      )
    );
  };

  return (
    <div className="schedule-table-container">
      <div className="description">
        {t("schedule_result_header", { date: headerDateLabel, totalWorkers: data?.total_worker_num })}
      </div>
      <div className={clsx("result-list", fetching ? "loading loading--list" : "")}>
        <div className="header-row flex">
          <div data-test-id="result-header-device" className="col1">
            {t("device")}
          </div>
          <div data-test-id="result-header-item" className="col2">
            {t("title")}
          </div>
          <div data-test-id="result-header-process" className={clsx("col3", !displayNumberOfWorkers ? "wide" : "")}>
            {t("content")}
          </div>
          {displayNumberOfWorkers ? (
            <div
              data-test-id="result-header-field-t1"
              className={clsx("col4", !customLabelNumberOfWorkers && "vertical-text")}
            >
              {customLabelNumberOfWorkers || t("number_of_workers")}
            </div>
          ) : null}
          <div data-test-id="result-header-result-type" className="vertical-text col5 last-column">
            {t("completion_or_handover")}
          </div>
          <div className="scroll-margin" />
        </div>
        <div key={`result-list-body-${isLoadingResults}`} className="body-area" ref={tableBodyRef}>
          {data?.list.map((item, index) => {
            return (
              <div key={`result-list-row-${index}`} className="body-row">
                <GridCell
                  className="col1"
                  align="left"
                  label={item.device_name}
                  setTooltip={setTooltip}
                  data-test-id={`result-${index + 1}-device`}
                >
                  {item.device_name}
                </GridCell>
                <GridCell
                  className="col2 break-word relative"
                  align="left"
                  label={item.item_name}
                  overflowLength={22}
                  setTooltip={setTooltip}
                  historyAlertColor={item.history_alert?.insert?.includes("record") && HISTORY_ALERT_COLOR.INSERT}
                  onClickHistory={() => handleClickHistory(item, "record", t("title"))}
                  data-test-id={`result-${index + 1}-item`}
                >
                  <a onClick={() => handleClickItemName(item)}>{omitString(item.item_name, 22)}</a>
                </GridCell>
                <GridCell
                  className={clsx("col3 break-word", !displayNumberOfWorkers ? "wide" : "")}
                  align="left"
                  label={item.process_name}
                  overflowLength={37}
                  setTooltip={setTooltip}
                  data-test-id={`result-${index + 1}-process`}
                >
                  {omitString(item.process_name, 37)}
                </GridCell>
                {displayNumberOfWorkers ? (
                  <GridCell
                    className="col4 relative"
                    label={item.field_t1?.toString()}
                    editable={isToday && item.roles?.some((v) => !!v["field_t1"])}
                    editing={textEditor.id && textEditor.id === item.result_task_id && textEditor.target === "field_t1"}
                    onEnter={() => textEditor.enter({ item, target: "field_t1" })}
                    onBlur={(value, e) => textEditor.submit(value, e)}
                    data-test-id={`result-${index + 1}-field-t1`}
                  />
                ) : undefined}
                <GridCell className="col5" data-test-id={`result-${index + 1}-result-type`}>
                  <div className="w-full">{item.result_type === 1 ? t("result_handover") : t("result_completion")}</div>
                </GridCell>
              </div>
            );
          })}
        </div>
        <div className="paging-footer">
          <FooterPager
            currentPage={page}
            maxPage={pageTotal}
            onPrev={onPrevPage}
            onNext={onNextPage}
            onJump={onJumpPage}
          />
        </div>
        {historyData && 0 < historyData.list.length ? (
          <ScheduleHistoryDialog
            title={historyData.title}
            target={historyData.target}
            historyList={historyData.list}
            onClose={() => {
              setHistoryData(null);
            }}
          />
        ) : (
          <></>
        )}
        <ReactTooltip
          id="schedule-cell-tooltip"
          className="matrix-task-detail"
          delayShow={500}
          effect="solid"
          offset={{ top: -5 }}
          getContent={() =>
            !tooltipText ? (
              <></>
            ) : (
              <div role="tooltip" className="schedule-tip in tooltip">
                <div className="tooltip-inner">
                  <div className="form-txt wrap">{tooltipText}</div>
                </div>
              </div>
            )
          }
        />
      </div>
    </div>
  );
};
